import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconFaceMask,
  IconPencil,
  IconTrash,
  IconX,
} from '@tabler/icons-react';

import { MedicalDoctor } from '@interfaces/medicalDoctors.interface';

import { isApiError } from '@api/index';
import {
  useDeleteMedicalDoctorMutation,
  useGetMedicalDoctorByIdQuery,
} from '@api/medicalDoctors.api';

import useAuth from '@hooks/useAuth';
import useLayoutProps from '@hooks/useLayoutProps';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertMedicalDoctorForm, {
  UPSERT_MEDICAL_DOCTOR_FORM_MODAL_ID,
} from '@components/medicalDoctors/UpsertMedicalDoctorForm';

export default function MedicalDoctorDetails() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { user, logout } = useAuth();

  const navigate = useNavigate();

  const { medicalDoctorId } = useParams();

  const canUpdateMedicalDoctor = useUserPermissionGuard(
    'MEDICAL_DOCTORS.UPDATE',
  );
  const canDeleteMedicalDoctor = useUserPermissionGuard(
    'MEDICAL_DOCTORS.DELETE',
  );

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: medicalDoctor, isLoading: isLoadingMedicalDoctor } =
    useGetMedicalDoctorByIdQuery(medicalDoctorId!);

  const [deleteMedicalDoctor, { isLoading: isLoadingDeleteMedicalDoctor }] =
    useDeleteMedicalDoctorMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleEditMedicalDoctorClick = useCallback(
    (medicalDoctor: MedicalDoctor) => {
      openModal({
        modalId: UPSERT_MEDICAL_DOCTOR_FORM_MODAL_ID,
        title: 'Modifica medico',
        children: <UpsertMedicalDoctorForm medicalDoctor={medicalDoctor} />,
        size: 'xl',
      });
    },
    [],
  );

  const handleDeleteMedicalDoctorClick = useCallback((id: string) => {
    openConfirmModal({
      title: 'Elimina medico',
      children:
        "Stai per eliminare il medico. Eliminerai il tuo account e non potrai più accedere. L'operazione non è reversibile.\nContinuare?",
      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deleteMedicalDoctor(id).unwrap();

          if (user?.medicalDoctorId === id) {
            await logout();
          }

          showNotification({
            title: 'Medico eliminato',
            message: 'Medico eliminato con successo',
          });

          navigate(-1);
        } catch (e) {
          if (isApiError(e)) {
            showNotification({
              title: 'Errore',
              message: e.data.message,
              color: 'red',
              icon: <IconX />,
            });
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconFaceMask />
        {medicalDoctor ? `${medicalDoctor.user.name}` : ''}
      </Group>
    ),
    [medicalDoctor],
  );

  const backLink = useMemo(
    () =>
      user?.role !== 'medicalDoctor'
        ? {
            title: 'Medici',
            to: '/medici',
          }
        : undefined,
    [user?.role],
  );

  useLayoutProps({
    title,
    backLink,
  });

  return isLoadingMedicalDoctor ? (
    <Loader />
  ) : !medicalDoctor ? (
    <Text fs="italic">Medico non trovato o non accessibile</Text>
  ) : (
    <>
      <Paper withBorder p="md" mb="xl">
        <Group justify="space-between" align="start">
          <Group gap="xl" align="start">
            <div>
              <Title order={4} mb="md">
                Informazioni medico
              </Title>
              <Text>
                <Text fw={700} span>
                  Nome:
                </Text>{' '}
                {medicalDoctor.user.name}
              </Text>
              <Text>
                <Text fw={700} span>
                  Email:
                </Text>{' '}
                {medicalDoctor.user.email}
              </Text>
              <Text>
                <Text fw={700} span>
                  Titolo:
                </Text>{' '}
                {medicalDoctor.title}
              </Text>
              <Text>
                <Text fw={700} span>
                  Specialità:
                </Text>{' '}
                {medicalDoctor.speciality.name}
              </Text>
              {medicalDoctor.socialSecurityNumber && (
                <Text>
                  <Text fw={700} span>
                    Codice fiscale:
                  </Text>{' '}
                  {medicalDoctor.socialSecurityNumber}
                </Text>
              )}
              {medicalDoctor.mobilePhone && (
                <Text>
                  <Text fw={700} span>
                    Telefono cellulare:
                  </Text>{' '}
                  {medicalDoctor.mobilePhone}
                </Text>
              )}
              {medicalDoctor.homePhone && (
                <Text>
                  <Text fw={700} span>
                    Telefono fisso:
                  </Text>{' '}
                  {medicalDoctor.homePhone}
                </Text>
              )}
              <Text>
                <Text fw={700} span>
                  Organizzazioni mediche:
                </Text>{' '}
                {medicalDoctor.medicalOrganizations
                  .map((org) => org.user.name)
                  .join(', ')}
              </Text>
            </div>

            <div>
              <Title order={4} mb="md">
                Indirizzo
              </Title>

              <Text>
                <Text fw={700} span>
                  Nazione:
                </Text>{' '}
                {medicalDoctor.countryCode}
              </Text>
              {medicalDoctor.stateProvince && (
                <Text>
                  <Text fw={700} span>
                    Provincia:
                  </Text>{' '}
                  {medicalDoctor.stateProvince}
                </Text>
              )}
              <Text>
                <Text fw={700} span>
                  Città:
                </Text>{' '}
                {medicalDoctor.locality}
              </Text>
              {medicalDoctor.postalCode && (
                <Text>
                  <Text fw={700} span>
                    CAP:
                  </Text>{' '}
                  {medicalDoctor.postalCode}
                </Text>
              )}
              <Text>
                <Text fw={700} span>
                  Indirizzo:
                </Text>{' '}
                {medicalDoctor.streetAddress1}
              </Text>
              {medicalDoctor.streetAddress2 && (
                <Text>
                  <Text fw={700} span>
                    Indirizzo (2):
                  </Text>{' '}
                  {medicalDoctor.streetAddress2}
                </Text>
              )}
            </div>
          </Group>

          <Stack>
            {canUpdateMedicalDoctor && (
              <Button
                leftSection={<IconPencil />}
                variant="outline"
                onClick={() => handleEditMedicalDoctorClick(medicalDoctor)}
              >
                Modifica
              </Button>
            )}
            {canDeleteMedicalDoctor && (
              <Button
                leftSection={<IconTrash />}
                color="red"
                onClick={() => handleDeleteMedicalDoctorClick(medicalDoctor.id)}
                loading={isLoadingDeleteMedicalDoctor}
              >
                Elimina
              </Button>
            )}
          </Stack>
        </Group>
      </Paper>
    </>
  );
}
